import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { GovernancaComposicaoEstado } from '../types';
import {
  MOCK_COMPOSICAO_VALORES,
  MOCK_LOTES_ATESTE,
  MOCK_FECHAMENTO_V2_RESUMO,
  MOCK_FECHAMENTO_V2_LISTA,
  construirCicloFinanceiroPipeline,
} from '../dados-simulados';

/** Pipeline do ciclo orçamentário-financeiro de uma competência — aba
 * "Pagamento & Selamento" e aba "Acompanhamento" do Fechamento Mensal V2. */
export function useCicloFinanceiroPipeline(mes: string) {
  return useQuery({
    queryKey: ['financeiro', 'fechamento-v2', 'ciclo-pipeline', mes],
    queryFn: async () => construirCicloFinanceiroPipeline(),
    enabled: mes.length > 0,
  });
}

export interface FiltroFechamentoV2Lista {
  etapa?: string;
  apenasAtrasados?: boolean;
}

/** FIN-023: listagem de Fechamento Mensal V2 — uma linha por competência ×
 * natureza (diferente da listagem V1, que é uma linha por DRE). */
export function useFechamentoV2Lista(filtro: FiltroFechamentoV2Lista = {}) {
  return useQuery({
    queryKey: ['financeiro', 'fechamento-v2', 'lista', filtro],
    queryFn: async () => {
      let itens = [...MOCK_FECHAMENTO_V2_LISTA];
      if (filtro.etapa) itens = itens.filter((i) => i.etapa === filtro.etapa);
      if (filtro.apenasAtrasados) itens = itens.filter((i) => i.atrasado);
      return itens;
    },
  });
}

/** Resumo do cabeçalho do Fechamento Mensal V2 — etapa atual, KPIs de
 * resultado/operação e ciclo SOF resumido. */
export function useFechamentoV2Resumo(mesReferencia: string, natureza: 'PF' | 'PJ') {
  const mesAno = mesReferencia.slice(0, 7);
  return useQuery({
    queryKey: ['financeiro', 'fechamento-v2', 'resumo', mesAno, natureza],
    queryFn: async () => MOCK_FECHAMENTO_V2_RESUMO[`${mesAno}__${natureza}`] ?? null,
    enabled: mesAno.length > 0,
  });
}

/** Aba "Ateste" (id interno composicao-valores) do Fechamento Mensal V2. */
export function useComposicaoValores(mes: string, natureza: 'PF' | 'PJ') {
  return useQuery({
    queryKey: ['financeiro', 'fechamento-v2', 'composicao-valores', mes, natureza],
    queryFn: async () => MOCK_COMPOSICAO_VALORES.filter((c) => c.mesReferencia.startsWith(mes)),
    enabled: mes.length > 0,
  });
}

const ESTADO_GOVERNANCA_INICIAL: GovernancaComposicaoEstado = {
  status: 'EM_CONFERENCIA',
  congeladoEm: null,
  congeladoPor: null,
  atestadoOperadorEm: null,
  atestadoOperadorPor: null,
  atestadoGestorEm: null,
  atestadoGestorPor: null,
  motivoDevolucao: null,
};

function chaveGovernanca(mes: string) {
  return ['financeiro', 'fechamento-v2', 'governanca', mes] as const;
}

/** Governança em 2 níveis do mês: Em conferência → Congelado → Aguardando Gestor DTP → Atestado. */
export function useGovernancaComposicao(mes: string) {
  return useQuery({
    queryKey: chaveGovernanca(mes),
    queryFn: async (): Promise<GovernancaComposicaoEstado> => ESTADO_GOVERNANCA_INICIAL,
    enabled: mes.length > 0,
    staleTime: Infinity,
  });
}

/** Usuário de exibição nos carimbos — não há sessão real ainda (Keycloak
 * fica pra quando a autenticação deste módulo existir). */
const USUARIO_MOCK = 'Usuário DTP';

export function useTransicaoGovernancaComposicao(mes: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (variaveis: {
      acao: 'CONGELAR' | 'ATESTAR' | 'LIBERAR' | 'APROVAR' | 'DEVOLVER';
      motivo?: string;
    }) => {
      await new Promise((resolve) => setTimeout(resolve, 500));
      return variaveis;
    },
    onSuccess: ({ acao, motivo }) => {
      queryClient.setQueryData<GovernancaComposicaoEstado>(chaveGovernanca(mes), (atual) => {
        const base = atual ?? ESTADO_GOVERNANCA_INICIAL;
        const agora = new Date().toISOString();
        switch (acao) {
          case 'CONGELAR':
            return { ...base, status: 'CONGELADO', congeladoEm: agora, congeladoPor: USUARIO_MOCK };
          case 'ATESTAR':
            return {
              ...base,
              status: 'AGUARDANDO_GESTOR',
              atestadoOperadorEm: agora,
              atestadoOperadorPor: USUARIO_MOCK,
            };
          case 'APROVAR':
            return {
              ...base,
              status: 'ATESTADO',
              atestadoGestorEm: agora,
              atestadoGestorPor: USUARIO_MOCK,
            };
          case 'DEVOLVER':
            return {
              ...base,
              status: 'CONGELADO',
              motivoDevolucao: motivo ?? null,
              atestadoOperadorEm: null,
              atestadoOperadorPor: null,
            };
          case 'LIBERAR':
            return { ...ESTADO_GOVERNANCA_INICIAL };
          default:
            return base;
        }
      });
    },
  });
}

/** Aba "Conciliação de atestes" do Fechamento Mensal V2 — planilhas PMSP importadas. */
export function useLotesAteste(mes: string, natureza: 'PF' | 'PJ') {
  return useQuery({
    queryKey: ['financeiro', 'fechamento-v2', 'conciliacao-atestes', mes, natureza],
    queryFn: async () => MOCK_LOTES_ATESTE,
    enabled: mes.length > 0,
  });
}

export function useImportarPlanilhaAteste() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (_arquivo: File) => {
      await new Promise((resolve) => setTimeout(resolve, 800));
      return { ok: true as const };
    },
    onSettled: () =>
      void queryClient.invalidateQueries({
        queryKey: ['financeiro', 'fechamento-v2', 'conciliacao-atestes'],
      }),
  });
}
